import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { setLocalStorageWithCleanup } from '../../utils/cacheManager';
import { useNavigation } from '../../context/NavigationContext';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const CACHE_KEY = 'cachedTickers.v3'; // Bumped version for hasFinancials field
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MAX_SEARCH_RESULTS = 50;

// Pre-configured filter for performance - matches ticker or name, limits results
const defaultFilterOptions = createFilterOptions({
  limit: MAX_SEARCH_RESULTS,
  stringify: (option) => `${option?.ticker || ''} ${option?.name || ''}`,
});

const normalizeExchange = (value) => {
  if (!value) return 'NASDAQ';

  const upperValue = String(value).toUpperCase();

  if (upperValue.includes('NYSE')) return 'NYSE';
  if (upperValue.includes('NASDAQ')) return 'NASDAQ';

  return upperValue;
};

const TICKER_EXCHANGE_OVERRIDES = {
  BMNR: 'NYSE',
};

const DEFAULT_TAB_OPTIONS = [
  { value: 0, label: 'Welcome' },
  { value: 1, label: 'Market News' },
  { value: 2, label: 'Stocks' },
  { value: 3, label: 'Crypto Analysis' },
  { value: 4, label: 'Watchlist' },
  { value: 5, label: 'Map' },
];

// Categorize ticker based on name and exchange patterns
const getTickerType = (option) => {
  if (!option || typeof option === 'string') return 'STOCK';
  
  const name = (option.name || '').toUpperCase();
  const exchange = (option.exchange || '').toUpperCase();
  const brandHints = [
    'ISHARES',
    'SPDR',
    'VANGUARD',
    'INVESCO',
    'PROSHARES',
    'SCHWAB',
    'DIREXION',
    'WISDOMTREE',
    'VANECK',
    'GLOBAL X',
    'FIRST TRUST',
    'ARK',
    'BITWISE',
    'GRANITESHARES',
    'ROUNDHILL',
    'DEFIANCE',
    'HASHDEX',
    'FRANKLIN',
    'CALAMOS',
    'LEVERAGESHARES',
    'TIDAL',
    'REX',
    'VOLATILITY SHARES',
    'UNITED STATES',
  ];
  const structureHints = ['ETF', 'ETN', 'FUND', 'TRUST', 'SHARES', 'INDEX', 'COMMODITY', 'BITCOIN', 'SOLANA', 'ETHEREUM', 'FUTURES', 'TREASURY', 'BOND', 'OIL', 'GOLD', 'SILVER', 'INCOME'];
  const hasWord = (text, word) => new RegExp(`\\b${String(word || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(text);
  const hasEtfWord = hasWord(name, 'ETF') || hasWord(name, 'ETN');
  const hasEtfBrand = brandHints.some((hint) => hasWord(name, hint));
  const hasEtfStructure = structureHints.some((hint) => hasWord(name, hint));
  const exchangeSuggestsEtf = ['ARCA', 'NYSEARCA', 'BATS', 'CBOE', 'NASDAQGM'].some((hint) => exchange.includes(hint));
  
  if (hasEtfWord || (hasEtfBrand && hasEtfStructure) || (exchangeSuggestsEtf && (hasEtfWord || (hasEtfBrand && hasEtfStructure) || name.includes('INDEX') || name.includes('COMMODITY') || name.includes('BITCOIN')))) {
    return 'ETF';
  }
  if (name.includes('SPAC') || name.includes('ACQUISITION CORP')) {
    return 'SPAC';
  }
  if (exchange.includes('OTC') || exchange.includes('PINK')) {
    return 'OTC';
  }
  if (name.includes('INDEX') || name.includes('COMPOSITE')) {
    return 'INDEX';
  }
  
  return 'STOCK';
};

const Header = ({ toggleTheme, darkMode, onTickerChange, selectedTicker: initialSelectedTicker = '', selectedExchange: initialSelectedExchange = 'NASDAQ' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { defaultMainTab, setDefaultMainTab, navigateToDefaultMainTab } = useNavigation();
  const theme = useTheme();
  const { user, logout } = useAuth0();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactLandscape = useMediaQuery('(max-height:480px) and (orientation: landscape)');
  const [selectedTicker, setSelectedTicker] = useState(initialSelectedTicker || '');
  const [exchange, setExchange] = useState(normalizeExchange(initialSelectedExchange));
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const isCryptoRoute = location.pathname.startsWith('/crypto-analysis');

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleOpenAccountSettings = () => {
    handleUserMenuClose();
    navigate('/account-settings');
  };

  const handleNavigateToDefaultTab = () => {
    navigateToDefaultMainTab();
  };

  const handleDefaultTabSelection = (tabValue) => {
    setDefaultMainTab(tabValue);
    handleUserMenuClose();
    handleMobileMenuClose();
  };

  useEffect(() => {
    fetchTickers();
  }, []);

  useEffect(() => {
    setSelectedTicker(initialSelectedTicker || '');
  }, [initialSelectedTicker]);

  useEffect(() => {
    setExchange(normalizeExchange(initialSelectedExchange));
  }, [initialSelectedExchange]);

  const getExchangeForTicker = useCallback((tickerValue) => {
    const normalizedTicker = (tickerValue || '').trim().toUpperCase();

    if (!normalizedTicker) {
      return normalizeExchange(initialSelectedExchange);
    }

    if (TICKER_EXCHANGE_OVERRIDES[normalizedTicker]) {
      return TICKER_EXCHANGE_OVERRIDES[normalizedTicker];
    }

    const matchedTicker = tickers.find((option) => option?.ticker === normalizedTicker);
    return normalizeExchange(matchedTicker?.exchange || initialSelectedExchange);
  }, [initialSelectedExchange, tickers]);

  useEffect(() => {
    if (!selectedTicker || tickers.length === 0) {
      return;
    }

    const resolvedExchange = getExchangeForTicker(selectedTicker);

    if (resolvedExchange !== exchange) {
      setExchange(resolvedExchange);

      if (resolvedExchange !== normalizeExchange(initialSelectedExchange)) {
        onTickerChange(selectedTicker, resolvedExchange);
      }
    }
  }, [exchange, getExchangeForTicker, initialSelectedExchange, onTickerChange, selectedTicker, tickers]);

  const fetchTickers = async () => {
    let hasUsableCache = false;

    try {
      setLoading(true);
      
      // Try to get cached data first
      const cachedData = localStorage.getItem(CACHE_KEY);
      
      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        
        // Require exchange metadata so header exchange reflects the selected ticker correctly.
        const isNewFormat =
          Array.isArray(data) &&
          data.length > 0 &&
          typeof data[0] === 'object' &&
          data[0].ticker &&
          Object.prototype.hasOwnProperty.call(data[0], 'exchange');
        
        if (isNewFormat && Date.now() - timestamp < CACHE_EXPIRY) {
          console.log('Using cached ticker data');
          setTickers(data);
          hasUsableCache = true;
          setLoading(false);
        } else {
          console.log('Cached ticker data expired or in old format, fetching fresh data');
        }
      }
      
      // Always fetch fresh tickers in the background so newly added symbols appear quickly.
      const response = await axios.get(`${API_BASE_URL}/api/tickers`);
      setTickers(response.data);
      
      // Cache the fresh data
      setLocalStorageWithCleanup(
        CACHE_KEY, 
        JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }),
        { targetRemovals: 10 }
      );
    } catch (error) {
      console.error('Error fetching tickers:', error);
      
      // If API call fails and cache was not already used, try cache as fallback.
      if (!hasUsableCache) {
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          const { data } = JSON.parse(cachedData);
          const hasExchangeMetadata =
            Array.isArray(data) &&
            data.length > 0 &&
            typeof data[0] === 'object' &&
            Object.prototype.hasOwnProperty.call(data[0], 'exchange');

          if (hasExchangeMetadata) {
            console.log('API error - falling back to cached data');
            setTickers(data);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTickerChange = (event, newValue) => {
    // Handle both string (freeSolo input) and object (selected from list) values
    const ticker = (typeof newValue === 'string' ? newValue : (newValue?.ticker || '')).trim().toUpperCase();
    
    const nextExchange = normalizeExchange(
      typeof newValue === 'string'
        ? getExchangeForTicker(newValue)
        : (newValue?.exchange || getExchangeForTicker(newValue?.ticker))
    );

    setSelectedTicker(ticker);
    setExchange(nextExchange);
    onTickerChange(ticker, nextExchange);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: isCompactLandscape ? 'row' : { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: isCompactLandscape ? 'center' : { xs: 'stretch', md: 'center' },
        gap: isCompactLandscape ? 0.5 : { xs: 0.75, sm: 1.5, md: 2 },
        padding: { xs: '0', sm: '6px 0' },
      }}
    >
        <Box
          role="button"
          tabIndex={0}
          aria-label="Go to your default tab"
          onClick={handleNavigateToDefaultTab}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleNavigateToDefaultTab();
            }
          }}
          sx={{ 
          display: isCompactLandscape ? 'none' : 'flex', 
          alignItems: 'center',
          gap: isCompactLandscape ? 0.75 : { xs: 1.25, sm: 2 },
          width: isCompactLandscape ? 'auto' : { xs: '100%', md: 'auto' },
          justifyContent: isCompactLandscape ? 'flex-start' : { xs: 'space-between', md: 'flex-start' },
          padding: isCompactLandscape ? '4px 8px' : { xs: '6px 10px', sm: '8px 16px' },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.secondary.main}08)`,
          border: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          outline: 'none',
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}66`,
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[4],
            background: `linear-gradient(135deg, ${theme.palette.primary.main}12, ${theme.palette.secondary.main}12)`,
          }
        }}>
          <Box sx={{
            padding: '6px',
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: theme.shadows[2],
          }}>
            <img 
              src="/logo-fbs.png" 
              alt="FBS Logo" 
              style={{ 
                height: isCompactLandscape ? 22 : isMobile ? 28 : 32,
                width: 'auto',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                transition: 'transform 0.2s ease',
              }} 
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                fontSize: isCompactLandscape ? '0.82rem' : { xs: '0.92rem', sm: '1.1rem' },
                background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              Company Analysis
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.66rem', sm: '0.7rem' },
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                opacity: 0.8,
              }}
            >
              Financial Insights
            </Typography>
          </Box>
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <IconButton 
                onClick={toggleTheme} 
                color="inherit"
                size="small"
                sx={{
                  flexShrink: 0,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
              </IconButton>
              <IconButton
                onClick={handleMobileMenuOpen}
                color="inherit"
                size="small"
                sx={{ flexShrink: 0 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Ticker Search Section */}
        {!isCryptoRoute && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            alignItems: 'center', 
            gap: isCompactLandscape ? 0.5 : { xs: 0.75, sm: 2 },
            flex: 1,
            width: isCompactLandscape ? 'auto' : '100%',
            maxWidth: isCompactLandscape ? 'none' : { xs: '100%', md: 640 },
            mx: isCompactLandscape ? 0 : { xs: 0, md: 2 },
            padding: isCompactLandscape ? '4px 6px' : { xs: '6px 8px', sm: '8px 16px' },
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}80, ${theme.palette.background.paper}40)`,
            border: `1px solid ${theme.palette.divider}`,
            backdropFilter: 'blur(10px)',
          }}>
            <Autocomplete
              freeSolo
              options={tickers}
              value={selectedTicker}
              onChange={handleTickerChange}
              loading={loading}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.ticker || '';
              }}
              filterOptions={defaultFilterOptions}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                const tickerType = getTickerType(option);
                
                return (
                  <li 
                    key={option?.ticker || option} 
                    {...restProps}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option?.ticker || option}
                        </Typography>
                        <Chip 
                          label={tickerType} 
                          size="small"
                          variant="outlined"
                          sx={{ 
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                      {option?.name && option.name !== option.ticker && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {option.name}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option?.exchange && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                            {normalizeExchange(option.exchange)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              isOptionEqualToValue={(option, value) => {
                const optionTicker = typeof option === 'string' ? option : option?.ticker;
                const valueTicker = typeof value === 'string' ? value : value?.ticker;
                return optionTicker === valueTicker;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search ticker or company name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              sx={{
                flex: 1,
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  minHeight: { xs: 38, sm: 'auto' },
                }
              }}
            />
            <Chip
              label={exchange || 'Exchange'}
              variant="outlined"
              sx={{
                minWidth: isCompactLandscape ? 80 : { xs: 92, sm: 120 },
                justifyContent: 'center',
                borderRadius: 2,
                backgroundColor: 'background.paper',
                height: isCompactLandscape ? 32 : { xs: 38, sm: 40 },
                flexShrink: 0,
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: isCompactLandscape ? 1 : { xs: 1.25, sm: 1.75 },
                },
              }}
            />
          </Box>
        )}

        <Box sx={{ 
          display: { xs: 'none', md: 'flex' }, 
          flexWrap: 'wrap',
          alignItems: 'center', 
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          gap: 1,
          width: { xs: '100%', md: 'auto' },
          padding: { xs: '8px 10px', sm: '4px' },
          borderRadius: 2,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}60, ${theme.palette.background.paper}30)`,
          border: `1px solid ${theme.palette.divider}`,
          backdropFilter: 'blur(10px)',
        }}>
          <IconButton 
            onClick={toggleTheme} 
            color="inherit"
            sx={{
              flexShrink: 0,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
              },
              transition: 'all 0.2s ease',
            }}
          >
            {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          
          {/* User Menu */}
          <Tooltip title={user?.name || user?.email || 'Account'}>
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{
                flexShrink: 0,
                p: 0.5,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}15)`,
                },
              }}
            >
              {user?.picture ? (
                <Avatar
                  src={user.picture}
                  alt={user?.name || 'User'}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <AccountCircleIcon sx={{ width: 32, height: 32 }} />
              )}
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled sx={{ opacity: 1 }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.name || 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={handleOpenAccountSettings}>
              <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
              Account Settings
            </MenuItem>
            {DEFAULT_TAB_OPTIONS.map((option) => (
              <MenuItem key={`default-tab-desktop-${option.value}`} onClick={() => handleDefaultTabSelection(option.value)}>
                <Box sx={{ width: 20, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {defaultMainTab === option.value ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
                </Box>
                Default Tab: {option.label}
              </MenuItem>
            ))}
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem disabled sx={{ opacity: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleOpenAccountSettings}>
            <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
            Account Settings
          </MenuItem>
          {DEFAULT_TAB_OPTIONS.map((option) => (
            <MenuItem key={`default-tab-mobile-${option.value}`} onClick={() => handleDefaultTabSelection(option.value)}>
              <Box sx={{ width: 20, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {defaultMainTab === option.value ? <CheckIcon sx={{ fontSize: 18 }} /> : null}
              </Box>
              Default Tab: {option.label}
            </MenuItem>
          ))}
          <MenuItem onClick={handleLogout}>
            <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
            Logout
          </MenuItem>
        </Menu>
    </Box>
  );
};

export default Header;